import { SortParams } from '../types';

/**
 * This is a placeholder base query function required by RTK Query's createApi.
 *
 * RTK Query requires a baseQuery parameter when using createApi, but in our implementation:
 * 1. We're using custom service classes for API communication
 * 2. Each endpoint overrides the default behavior with its own queryFn implementation
 * 3. The actual API calls are handled within those queryFn implementations
 *
 * This is a standard pattern for service-based invocations with RTK Query, allowing us to:
 * - Leverage existing service layer abstractions
 * - Benefit from RTK Query's caching, invalidation, and hook generation
 * - Keep API implementation details encapsulated in dedicated service classes
 *
 * Because we're bypassing RTK Query's built-in fetching mechanism, this placeholder function
 * is never actually called, but it satisfies the API requirement.
 */
export const baseQueryStub = () => {
  return Promise.resolve({ data: null });
};

/**
 * Utility function to determine if a query should be refetched based on sort parameter changes
 *
 * @param currentArg - The current query argument
 * @param previousArg - The previous query argument
 * @returns true if a refetch is needed, false otherwise
 */
export const shouldRefetchOnSortChange = ({
  currentArg,
  previousArg,
}: {
  currentArg?: { sort?: SortParams };
  previousArg?: { sort?: SortParams };
}) => {
  // If there's no previous argument, we need to fetch
  if (!previousArg) return true;
  // If there's no current argument, we shouldn't fetch
  if (!currentArg) return false;

  const prevSort = previousArg.sort;
  const currentSort = currentArg.sort;

  // If sort state changes (added, removed, or modified), force refetch
  if (!prevSort && currentSort) return true;
  if (prevSort && !currentSort) return true;
  if (prevSort && currentSort) {
    return prevSort.field !== currentSort.field || prevSort.direction !== currentSort.direction;
  }

  return false;
};
